import React, {useEffect} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Button, {Kind} from '../LegacyButton';

import styles from './Modal.module.scss';

export type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  buttonClose?: boolean;
  buttonFloatText?: string;
  onButtonFloatClick?: () => void;
  title?: string;
  isButtonFloatDisabled?: boolean;
  buttonKind?: Kind;
  userRole?: string;
  vw?: number;
  vh?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  clickOutsideToClose: boolean;
  onScrollEnd?(): void;
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  buttonClose = true,
  title,
  buttonFloatText,
  onButtonFloatClick,
  isButtonFloatDisabled = false,
  buttonKind = 'accentPrimary',
  userRole = 'student',
  children,
  clickOutsideToClose,
  vw,
  vh,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  onScrollEnd,
}) => {
  const style: React.CSSProperties = {
    ...(vw ? {width: `${vw}vw`} : {}),
    ...(vh ? {height: `${vh}vh`} : {}),
    ...(minWidth ? {minWidth: `${minWidth}px`} : {}),
    ...(minHeight ? {minHeight: `${minHeight}px`} : {}),
    ...(maxWidth ? {maxWidth: `${maxWidth}px`} : {}),
    ...(maxHeight ? {maxHeight: `${maxHeight}px`} : {}),
  };

  const hasButtonFloat = buttonFloatText && onButtonFloatClick;

  // Control body overflow when modal is open to prevent scroll behind modal
  // and ensure tour popover doesn't interfere with modal interaction
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return function cleanUp() {
      document.body.removeAttribute('style');
    };
  }, [isOpen]);

  function onScroll(event: React.UIEvent<HTMLDivElement>) {
    const container: Partial<HTMLDivElement> = event.target;

    if (
      container.scrollHeight! - container.scrollTop! ===
      container.clientHeight
    ) {
      onScrollEnd?.();
    }
  }

  return (
    <div
      data-testid="container"
      className={`${styles.container} ${isOpen ? styles.open : ''}`}
    >
      {isOpen && (
        <div className={`${styles.modal}`} role="dialog" style={style}>
          {buttonClose || title ? (
            <div
              className={`${styles.header} ${title ? styles.withTitle : ''} ${
                buttonClose ? styles.withButtonClose : ''
              }`}
            >
              {title && <h4 className={styles.title}>{title}</h4>}

              {buttonClose && (
                <button
                  className={styles.buttonClose}
                  onClick={onClose}
                  data-testid="buttonClose"
                  type="button"
                >
                  <FontAwesomeIcon icon="times" size="lg" color="#666" />
                </button>
              )}
            </div>
          ) : null}
          <div className={styles.content}>
            <div
              onScroll={onScroll}
              className={`${styles.children} ${
                hasButtonFloat ? styles.hasButtonFloat : ''
              }`}
              data-testid="modal-children"
            >
              {children}
            </div>
            {onButtonFloatClick && buttonFloatText && (
              <footer>
                <div className={styles.button}>
                  <Button
                    userRole={userRole}
                    kind={buttonKind}
                    text={buttonFloatText}
                    onClick={onButtonFloatClick}
                    disabled={isButtonFloatDisabled}
                  />
                </div>
              </footer>
            )}
          </div>
        </div>
      )}
      <div
        data-testid="background"
        className={styles.background}
        onMouseDown={clickOutsideToClose ? onClose : undefined}
        role="presentation"
      />
    </div>
  );
};

export default Modal;
